import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function CareersPage() {
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openPositions = [
    {
      id: 1,
      title: 'Senior ServiceNow Developer',
      department: 'Technical',
      location: 'Dallas, TX / Remote',
      type: 'Full-time',
      experience: '5+ years',
      description: 'Lead ServiceNow development projects and mentor junior developers in implementing complex solutions.',
      requirements: [
        'ServiceNow Certified Application Developer',
        'Expert in JavaScript, Flow Designer, and Integration Hub',
        'Experience with ITSM, ITOM, or CSM modules',
        'Strong problem-solving and communication skills'
      ]
    },
    {
      id: 2,
      title: 'ServiceNow Solutions Architect',
      department: 'Technical',
      location: 'Calgary, AB / Remote',
      type: 'Full-time',
      experience: '7+ years',
      description: 'Design and architect enterprise-scale ServiceNow solutions for Fortune 500 clients.',
      requirements: [
        'ServiceNow Certified Implementation Specialist',
        'Proven track record of successful implementations',
        'Deep understanding of ServiceNow platform capabilities',
        'Excellent client-facing and presentation skills'
      ]
    },
    {
      id: 3,
      title: 'ITSM Consultant',
      department: 'Consulting',
      location: 'London, UK / Hybrid',
      type: 'Full-time',
      experience: '3+ years',
      description: 'Work with clients to optimize their IT Service Management processes using ServiceNow.',
      requirements: [
        'ITIL v4 Foundation certification',
        'Experience with ServiceNow ITSM module',
        'Strong analytical and process improvement skills',
        'Ability to work independently and in teams'
      ]
    },
    {
      id: 4,
      title: 'Business Analyst',
      department: 'Consulting',
      location: 'Bangalore, IN / Remote',
      type: 'Full-time',
      experience: '2+ years',
      description: 'Gather requirements and translate business needs into ServiceNow solutions.',
      requirements: [
        'Experience in business analysis or consulting',
        'Understanding of ServiceNow platform',
        'Excellent documentation and communication skills',
        'Ability to work with diverse stakeholders'
      ]
    },
    {
      id: 5,
      title: 'Project Manager',
      department: 'Management',
      location: 'Dallas, TX / Hybrid',
      type: 'Full-time',
      experience: '5+ years',
      description: 'Lead ServiceNow implementation projects from initiation to successful delivery.',
      requirements: [
        'PMP or equivalent certification preferred',
        'Experience managing ServiceNow projects',
        'Strong leadership and organizational skills',
        'Proven track record of on-time, on-budget delivery'
      ]
    },
    {
      id: 6,
      title: 'ServiceNow Administrator',
      department: 'Technical',
      location: 'Remote',
      type: 'Full-time',
      experience: '2+ years',
      description: 'Maintain and support ServiceNow instances for multiple clients.',
      requirements: [
        'ServiceNow System Administrator certification',
        'Experience with platform configuration and maintenance',
        'Knowledge of ServiceNow best practices',
        'Strong troubleshooting skills'
      ]
    },
    {
      id: 7,
      title: 'ITOM Specialist',
      department: 'Technical',
      location: 'Calgary, AB / Remote',
      type: 'Full-time',
      experience: '4+ years',
      description: 'Implement and optimize ServiceNow ITOM solutions including Discovery and Service Mapping.',
      requirements: [
        'ServiceNow ITOM certification',
        'Experience with Discovery, Service Mapping, and CMDB',
        'Understanding of IT infrastructure and cloud platforms',
        'Strong technical and analytical skills'
      ]
    },
    {
      id: 8,
      title: 'Talent Acquisition Specialist',
      department: 'HR',
      location: 'Dallas, TX / Hybrid',
      type: 'Full-time',
      experience: '3+ years',
      description: 'Source and recruit top ServiceNow talent to support our growing business.',
      requirements: [
        'Experience recruiting for technical roles',
        'Understanding of ServiceNow ecosystem',
        'Strong networking and relationship-building skills',
        'Proficiency with ATS and recruiting tools'
      ]
    }
  ];

  const benefits = [
    {
      icon: 'ri-heart-pulse-line',
      title: 'Health & Wellness',
      description: 'Comprehensive health, dental, and vision insurance for you and your family'
    },
    {
      icon: 'ri-time-line',
      title: 'Flexible Schedule',
      description: 'Work-life balance with flexible hours and remote work options'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Learning & Development',
      description: 'ServiceNow certifications, training programs, and conference attendance'
    },
    {
      icon: 'ri-plane-line',
      title: 'Paid Time Off',
      description: 'Generous vacation policy, sick leave, and paid holidays'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Competitive Salary',
      description: 'Market-leading compensation with performance bonuses'
    },
    {
      icon: 'ri-team-line',
      title: 'Collaborative Culture',
      description: 'Work with talented professionals in a supportive environment'
    },
    {
      icon: 'ri-home-office-line',
      title: 'Remote Work',
      description: 'Fully remote positions available with home office stipend'
    },
    {
      icon: 'ri-trophy-line',
      title: 'Career Growth',
      description: 'Clear career progression paths and mentorship opportunities'
    }
  ];

  const departments = [
    { value: 'all', label: 'All Departments' },
    { value: 'Technical', label: 'Technical' },
    { value: 'Consulting', label: 'Consulting' },
    { value: 'Management', label: 'Management' },
    { value: 'HR', label: 'HR' }
  ];

  const filteredPositions = selectedDepartment === 'all' 
    ? openPositions 
    : openPositions.filter(pos => pos.department === selectedDepartment);

  const [expandedPosition, setExpandedPosition] = useState<number | null>(null);

  const togglePosition = (id: number) => {
    setExpandedPosition(expandedPosition === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-80 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b94645911c8402e21a7844d990eeae08.jpg"
            alt="Join Our Team"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Join Our Team</h1>
          <p className="text-xl md:text-2xl leading-relaxed">
            Build your career with a leading ServiceNow consulting firm and work on transformative projects
          </p>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Why ClearPath Consulting?</h2>
            <p className="text-xl text-gray-600">Join a team that values innovation, growth, and excellence</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-rocket-line text-3xl text-sky-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Cutting-Edge Projects</h3>
              <p className="text-gray-600 leading-relaxed">
                Work on innovative ServiceNow implementations for Fortune 500 companies and industry leaders
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-line-chart-line text-3xl text-sky-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Career Advancement</h3>
              <p className="text-gray-600 leading-relaxed">
                Clear career paths with opportunities for growth, leadership roles, and skill development
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-global-line text-3xl text-sky-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Global Opportunities</h3>
              <p className="text-gray-600 leading-relaxed">
                Work with international teams across North America, Europe, and Asia
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-star-line text-3xl text-sky-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Expert Mentorship</h3>
              <p className="text-gray-600 leading-relaxed">
                Learn from senior consultants and certified ServiceNow professionals
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-scales-3-line text-3xl text-sky-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Work-Life Balance</h3>
              <p className="text-gray-600 leading-relaxed">
                Flexible schedules, remote work options, and generous time-off policies
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-award-line text-3xl text-sky-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Recognition & Rewards</h3>
              <p className="text-gray-600 leading-relaxed">
                Competitive compensation, performance bonuses, and employee recognition programs
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6 bg-sky-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Benefits & Perks</h2>
            <p className="text-xl text-gray-600">We invest in our people with comprehensive benefits</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer">
                <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${benefit.icon} text-2xl text-sky-600`}></i>
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">{benefit.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Open Positions</h2>
            <p className="text-xl text-gray-600 mb-8">Find your next opportunity with ClearPath Consulting</p>
            
            {/* Department Filter */}
            <div className="flex flex-wrap justify-center gap-3">
              {departments.map((dept) => (
                <button
                  key={dept.value}
                  onClick={() => setSelectedDepartment(dept.value)}
                  className={`px-6 py-2 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    selectedDepartment === dept.value
                      ? 'bg-sky-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {dept.label}
                </button>
              ))}
            </div>
          </div>
          
          <div className="space-y-4">
            {filteredPositions.map((position) => (
              <div key={position.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div 
                  className="p-6 cursor-pointer"
                  onClick={() => togglePosition(position.id)}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{position.title}</h3>
                      <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <i className="ri-briefcase-line"></i>
                          {position.department}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-map-pin-line"></i>
                          {position.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {position.type}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i>
                          {position.experience}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button className="px-6 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors whitespace-nowrap cursor-pointer">
                        Apply Now
                      </button>
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className={`ri-arrow-${expandedPosition === position.id ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
                      </div>
                    </div>
                  </div>
                </div>
                
                {expandedPosition === position.id && (
                  <div className="px-6 pb-6 border-t border-gray-100 pt-6">
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">About the Role</h4>
                        <p className="text-gray-700 leading-relaxed">{position.description}</p>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">Requirements</h4>
                        <ul className="space-y-2">
                          {position.requirements.map((req, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-gray-700">
                              <i className="ri-checkbox-circle-fill text-sky-600 mt-1"></i>
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="pt-4">
                        <a
                          href={`mailto:careers@clearpathconsulting-global.com?subject=Application for ${position.title}`}
                          className="inline-block px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Apply via Email
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {filteredPositions.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-xl text-gray-500">No positions available in this department</p>
            </div>
          )}
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-slate-700 to-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-white">Our Culture</h2>
              <p className="text-lg text-white/90 mb-6 leading-relaxed">
                At ClearPath Consulting, we foster a culture of collaboration, innovation, and continuous learning. Our team members are passionate about ServiceNow and committed to delivering exceptional results for our clients.
              </p>
              <p className="text-lg text-white/90 mb-6 leading-relaxed">
                We believe in empowering our employees to take ownership of their work, pursue professional development, and maintain a healthy work-life balance. Join us and be part of a team that values your contributions and supports your growth.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
                  <div className="text-3xl font-bold text-white">50+</div>
                  <div className="text-sm text-white/80">Team Members</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
                  <div className="text-3xl font-bold text-white">4</div>
                  <div className="text-sm text-white/80">Global Offices</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
                  <div className="text-3xl font-bold text-white">95%</div>
                  <div className="text-sm text-white/80">Employee Satisfaction</div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://readdy.ai/api/search-image?query=happy%20diverse%20team%20members%20celebrating%20success%20in%20modern%20office%20with%20high-fives%20and%20smiles%20showing%20positive%20workplace%20culture%20and%20team%20collaboration%20in%20bright%20professional%20environment&width=400&height=300&seq=culture-001&orientation=landscape"
                alt="Team Collaboration"
                className="w-full h-48 object-cover object-top rounded-lg"
              />
              <img
                src="https://readdy.ai/api/search-image?query=professional%20training%20session%20with%20ServiceNow%20consultants%20learning%20new%20skills%20in%20modern%20conference%20room%20with%20instructor%20and%20laptops%20showing%20continuous%20learning%20and%20development%20culture&width=400&height=300&seq=culture-002&orientation=landscape"
                alt="Learning & Development"
                className="w-full h-48 object-cover object-top rounded-lg mt-8"
              />
              <img
                src="https://readdy.ai/api/search-image?query=multicultural%20diverse%20team%20of%20professionals%20including%20Asian%20African%20American%20Hispanic%20and%20Caucasian%20colleagues%20enjoying%20company%20social%20event%20with%20refreshments%20laughing%20and%20networking%20in%20modern%20bright%20office%20lounge%20area%20showing%20inclusive%20workplace%20culture&width=400&height=300&seq=culture-003-diverse&orientation=landscape"
                alt="Team Events"
                className="w-full h-48 object-cover object-top rounded-lg"
              />
              <img
                src="https://readdy.ai/api/search-image?query=remote%20worker%20in%20comfortable%20home%20office%20setup%20with%20laptop%20and%20coffee%20showing%20flexible%20work%20environment%20and%20work%20from%20home%20culture%20with%20natural%20lighting%20and%20modern%20workspace&width=400&height=300&seq=culture-004&orientation=landscape"
                alt="Remote Work"
                className="w-full h-48 object-cover object-top rounded-lg mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6 text-gray-900">Don't See the Right Position?</h2>
          <p className="text-xl text-gray-700 mb-8 leading-relaxed">
            We're always looking for talented professionals to join our team. Send us your resume to careers@clearpathconsulting-global.com&nbsp;We will assess if you're a good fit.
          </p>
          <a
            href="mailto:careers@clearpathconsulting-global.com"
            className="inline-block px-8 py-4 bg-slate-700 text-white text-lg font-semibold rounded-lg hover:bg-slate-800 transition-colors duration-300 whitespace-nowrap cursor-pointer"
          >
            Send Your Resume
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
